/**
 * Blueprint da landing de captação.
 *
 * Este arquivo é a fonte única da verdade: descreve QUAIS blocos existem e
 * QUAIS campos cada um tem. A landing renderiza a partir daqui e o editor
 * do painel gera os formulários iterando sobre `BLOCKS` — acrescentar um
 * campo novo é uma linha aqui, sem tocar no editor.
 *
 * A estrutura (14 blocos, na ordem) veio do estudo de uma landing de
 * recrutamento de revenda; o padrão que se repete é
 * `headline → subheadline → conteúdo → CTA`.
 */

import type {
  ComboName, ColorName, PadName, ShapeName, PesoCampo,
  EntrelinhaCampo, EspacamentoCampo,
} from './theme'
import { TAM_MIN, TAM_MAX } from './theme'

// ─── Campos ────────────────────────────────────────────────────────────────

export type SimpleFieldType = 'text' | 'textarea' | 'image' | 'link'

/**
 * Alinhamento de um campo isolado. Mais solto que o do bloco, que só tem
 * esquerda e centro: um texto solto numa linha compartilhada — a assinatura do
 * rodapé, ao lado da logo — também precisa poder ir para a direita.
 */
export type AlignCampo = 'left' | 'center' | 'right'

/**
 * Aparência de um campo isolado, independente do estilo do bloco.
 *
 * Serve para duas coisas ao mesmo tempo: no blueprint, cada chave PRESENTE liga
 * aquele controle no editor e declara o padrão do campo; na config salva, o
 * mesmo formato guarda o que o usuário escolheu. Chave ausente no blueprint =
 * controle que nem aparece, então cada campo mostra só o que faz sentido nele.
 *
 * Todos os valores são fechados. Não existe cor livre nem tamanho em px: é o
 * que mantém a landing dentro do guia de marca por construção.
 */
export interface EstiloCampo {
  align?: AlignCampo
  /** Papel tipográfico: corpo (Light), título (Bold) ou display (Black). */
  peso?: PesoCampo
  /** Tamanho da fonte em px, na tela grande. Ver `tamanhoFluido`. */
  tam?: number
  /** Cor do texto em hex. Vazio herda a cor da seção. */
  cor?: string
  italico?: boolean
  sublinhado?: boolean
  /** CAIXA ALTA. É transformação visual: o texto salvo continua como digitado. */
  caixaAlta?: boolean
  entrelinha?: EntrelinhaCampo
  espacamento?: EspacamentoCampo
  /**
   * Família da fonte. Vazio usa a do tema (Earl Sans). Fora isso, é o `nome` de
   * uma das fontes enviadas em `LandingConfig.fontes`.
   */
  fonte?: string
}

/**
 * Fonte enviada pelo painel. Fica na config, e não no tema, porque é conteúdo
 * que o usuário sobe — o tema é código.
 */
export interface FonteEnviada {
  /** Nome da família, usado no @font-face e no seletor. */
  nome: string
  /** URL relativa em /uploads/landing. */
  url: string
}

/**
 * Chave de estilo de um campo. Um campo de lista usa `campo.subcampo`, sem o
 * índice: os cartões de uma lista compartilham a aparência de propósito —
 * estilizar cartão a cartão desalinharia a fileira, que é justamente o defeito
 * que o resto do arquivo se dá ao trabalho de evitar.
 */
export function chaveEstilo(campo: string, subcampo?: string): string {
  return subcampo ? `${campo}.${subcampo}` : campo
}

export interface SimpleField {
  key: string
  label: string
  type: SimpleFieldType
  /** Texto de ajuda mostrado abaixo do campo no editor. */
  hint?: string
  /**
   * Aparência própria deste campo. Presente = o editor desenha os controles
   * sozinho, um para cada chave declarada. Só vale em campo de primeiro nível:
   * dentro de uma lista a escolha seria por item, não por campo.
   */
  estilo?: EstiloCampo
}

export interface ListField {
  key: string
  label: string
  type: 'list'
  /** Nome no singular, usado no botão "Adicionar ___". */
  itemLabel: string
  /** Campos de cada item da lista. */
  fields: SimpleField[]
}

export type Field = SimpleField | ListField

export function isListField(f: Field): f is ListField {
  return f.type === 'list'
}

// ─── Blocos ────────────────────────────────────────────────────────────────

export type BlockId =
  | 'header' | 'hero' | 'passos' | 'comoPedir' | 'suporte' | 'pedidoMinimo'
  | 'calculadora' | 'videos' | 'produtos' | 'materiais' | 'depoimentos'
  | 'jaTenho' | 'faq' | 'ctaFinal' | 'footer'

export interface BlockDef {
  id: BlockId
  /** Número de exibição no editor (01..14), fixo mesmo se a ordem mudar. */
  n: string
  label: string
  hint: string
  fields: Field[]
  /** Controles de estilo que fazem sentido nesse bloco. */
  caps?: {
    /** Bloco tem imagem ao lado do texto → habilita "lado da imagem". */
    mediaSide?: boolean
    /** Bloco tem altura ajustável (só o hero). */
    height?: boolean
    /**
     * Bloco desenha lâmina (`Section`) e portanto lê `fundoImg`. Padrão: sim.
     *
     * `false` só no header, que desenha o próprio `<header>` fixo e nunca passa
     * por `Section` — ali os controles de fundo existiam e não faziam nada.
     */
    fundo?: boolean
  }
  /** Estilo inicial do bloco no blueprint vazio. */
  defaults: Partial<BlockStyle>
}

const linkFields = (): SimpleField[] => [
  { key: 'ctaLabel', label: 'Texto do botão', type: 'text' },
  { key: 'ctaHref',  label: 'Destino do botão', type: 'link', hint: 'URL completa ou âncora (#seja-consultora)' },
]

export const BLOCKS: BlockDef[] = [
  {
    id: 'header', n: '01', label: 'Header',
    hint: 'Barra fixa no topo, com logo, links de âncora e botão principal.',
    // Único bloco que não usa `Section`: desenha o próprio <header> fixo.
    caps: { fundo: false },
    defaults: { combo: 'creme', align: 'left', pad: 'compact', shape: 'none' },
    fields: [
      { key: 'logo', label: 'Logo', type: 'image' },
      {
        key: 'links', label: 'Links do menu', type: 'list', itemLabel: 'link',
        fields: [
          { key: 'label', label: 'Texto', type: 'text' },
          { key: 'href',  label: 'Âncora ou URL', type: 'link' },
        ],
      },
      ...linkFields(),
    ],
  },
  {
    id: 'hero', n: '02', label: 'Hero',
    // Dizia "imagem de fundo" e entregava foto AO LADO do texto, em duas
    // colunas — a inconsistência de vocabulário que sobrevivia desde o começo.
    // Fundo de verdade existe, mas mora no estilo da lâmina, em toda seção.
    hint: 'Primeira dobra: foto ao lado do texto, chamada principal e CTA.',
    caps: { height: true },
    defaults: { textura: true, combo: 'marrom', align: 'left', pad: 'amplo', shape: 'none', height: 72 },
    fields: [
      {
        key: 'galeria', label: 'Imagens do carrossel', type: 'list', itemLabel: 'imagem',
        fields: [{ key: 'imagem', label: 'Imagem', type: 'image' }],
      },
      {
        key: 'imagem', label: 'Foto ao lado do texto', type: 'image',
        hint: 'Aparece ao lado da chamada. Só é usada quando o carrossel acima está vazio.',
      },
      { key: 'headline', label: 'Chamada principal', type: 'text' },
      { key: 'sub',      label: 'Subtítulo', type: 'textarea' },
      ...linkFields(),
    ],
  },
  {
    id: 'passos', n: '03', label: 'Passos em cards',
    hint: 'Os poucos passos para começar, um card por passo.',
    defaults: { combo: 'creme', align: 'center', pad: 'normal', shape: 'none' },
    fields: [
      { key: 'headline', label: 'Título da seção', type: 'text' },
      { key: 'sub',      label: 'Subtítulo', type: 'textarea' },
      {
        key: 'cards', label: 'Passos', type: 'list', itemLabel: 'passo',
        fields: [
          { key: 'titulo', label: 'Título', type: 'text' },
          { key: 'texto',  label: 'Descrição', type: 'textarea' },
        ],
      },
    ],
  },
  {
    id: 'comoPedir', n: '04', label: 'Como fazer o pedido',
    hint: 'Lista numerada de instruções ao lado de uma imagem.',
    caps: { mediaSide: true },
    defaults: { combo: 'laranja', align: 'left', pad: 'normal', shape: 'none' },
    fields: [
      { key: 'headline', label: 'Título da seção', type: 'text' },
      {
        key: 'passos', label: 'Instruções', type: 'list', itemLabel: 'instrução',
        fields: [{ key: 'texto', label: 'Instrução', type: 'textarea' }],
      },
      { key: 'imagem', label: 'Imagem de apoio', type: 'image' },
      ...linkFields(),
    ],
  },
  {
    id: 'suporte', n: '05', label: 'Suporte',
    hint: 'Reforça que a consultora não fica sozinha — contato direto.',
    defaults: { combo: 'creme', align: 'center', pad: 'normal', shape: 'none' },
    fields: [
      { key: 'headline',       label: 'Título da seção', type: 'text' },
      { key: 'texto',          label: 'Texto', type: 'textarea' },
      { key: 'whatsappLabel',  label: 'Texto do botão de WhatsApp', type: 'text' },
      { key: 'whatsappHref',   label: 'Link do WhatsApp', type: 'link', hint: 'https://wa.me/55...' },
      { key: 'telefone',       label: 'Telefone exibido', type: 'text' },
    ],
  },
  {
    id: 'pedidoMinimo', n: '06', label: 'Pedido mínimo',
    hint: 'Destaque do valor de entrada e das condições de pagamento.',
    defaults: { textura: true, combo: 'marrom', align: 'center', pad: 'normal', shape: 'none' },
    fields: [
      { key: 'headline',  label: 'Título da seção', type: 'text' },
      { key: 'valor',     label: 'Valor em destaque', type: 'text', hint: 'Ex.: R$ 360,00' },
      { key: 'texto',     label: 'Texto', type: 'textarea' },
      { key: 'pagamento', label: 'Condições de pagamento', type: 'textarea' },
      ...linkFields(),
    ],
  },
  {
    id: 'calculadora', n: '07', label: 'Calculadora de ganhos',
    hint: 'Simulador: a pessoa escolhe a faixa, informa a venda e vê o lucro.',
    defaults: { textura: true, combo: 'laranja', align: 'center', pad: 'normal', shape: 'none' },
    fields: [
      { key: 'headline', label: 'Título da seção', type: 'text' },
      { key: 'sub',      label: 'Subtítulo', type: 'textarea' },
      {
        key: 'faixas', label: 'Faixas de comissão', type: 'list', itemLabel: 'faixa',
        fields: [
          { key: 'nome',    label: 'Nome da faixa', type: 'text', hint: 'Ex.: Bronze' },
          { key: 'percent', label: 'Percentual', type: 'text', hint: 'Só o número. Ex.: 20' },
        ],
      },
      { key: 'inputLabel',     label: 'Rótulo do campo de venda', type: 'text' },
      { key: 'resultadoLabel', label: 'Rótulo do resultado', type: 'text' },
      { key: 'botaoLabel',     label: 'Texto do botão', type: 'text' },
    ],
  },
  {
    id: 'videos', n: '08', label: 'Vídeos',
    hint: 'Grade de vídeos curtos de prova social.',
    defaults: { textura: true, combo: 'creme', align: 'center', pad: 'normal', shape: 'none' },
    fields: [
      { key: 'headline', label: 'Título da seção', type: 'text' },
      { key: 'sub',      label: 'Subtítulo', type: 'textarea' },
      {
        key: 'videos', label: 'Vídeos', type: 'list', itemLabel: 'vídeo',
        fields: [
          { key: 'url',   label: 'URL do vídeo', type: 'link', hint: 'Arquivo .mp4 servido em /uploads' },
          { key: 'capa',  label: 'Imagem de capa', type: 'image' },
        ],
      },
    ],
  },
  {
    id: 'produtos', n: '09', label: 'Produtos',
    hint: 'Vitrine de produtos sugeridos, com total do combo.',
    defaults: { combo: 'creme', align: 'center', pad: 'normal', shape: 'none' },
    fields: [
      { key: 'headline', label: 'Título da seção', type: 'text' },
      { key: 'sub',      label: 'Subtítulo', type: 'textarea' },
      {
        key: 'produtos', label: 'Produtos', type: 'list', itemLabel: 'produto',
        fields: [
          { key: 'imagem', label: 'Imagem', type: 'image' },
          { key: 'nome',   label: 'Nome', type: 'text' },
          { key: 'codigo', label: 'Código', type: 'text' },
          { key: 'preco',  label: 'Preço', type: 'text' },
        ],
      },
      { key: 'totalLabel',    label: 'Rótulo do total', type: 'text' },
      { key: 'totalValor',    label: 'Valor total', type: 'text' },
      { key: 'parcelamento',  label: 'Parcelamento', type: 'text' },
      ...linkFields(),
    ],
  },
  {
    id: 'materiais', n: '10', label: 'Materiais de apoio',
    hint: 'Cards com revista, materiais de venda, lives etc.',
    defaults: { textura: true, combo: 'marrom', align: 'center', pad: 'normal', shape: 'none' },
    fields: [
      { key: 'headline', label: 'Título da seção', type: 'text' },
      { key: 'sub',      label: 'Subtítulo', type: 'textarea' },
      {
        key: 'cards', label: 'Materiais', type: 'list', itemLabel: 'material',
        fields: [
          { key: 'imagem', label: 'Imagem', type: 'image' },
          { key: 'titulo', label: 'Título', type: 'text' },
          { key: 'texto',  label: 'Descrição', type: 'textarea' },
          { key: 'href',   label: 'Link', type: 'link' },
        ],
      },
    ],
  },
  {
    id: 'depoimentos', n: '11', label: 'Depoimentos',
    hint: 'Prova social de quem já revende.',
    defaults: { combo: 'creme', align: 'center', pad: 'normal', shape: 'none' },
    fields: [
      { key: 'headline', label: 'Título da seção', type: 'text' },
      { key: 'badge',    label: 'Selo de destaque', type: 'text', hint: 'Ex.: + de 20.000 consultoras' },
      {
        key: 'cards', label: 'Depoimentos', type: 'list', itemLabel: 'depoimento',
        fields: [
          { key: 'foto',  label: 'Foto', type: 'image' },
          { key: 'texto', label: 'Depoimento', type: 'textarea' },
          { key: 'nome',  label: 'Nome', type: 'text' },
          { key: 'desde', label: 'Consultora desde', type: 'text' },
        ],
      },
    ],
  },
  {
    id: 'jaTenho', n: '15', label: 'Já tenho cadastro',
    hint: 'Autoatendimento para quem já é consultora: reativar cadastro, recuperar senha, tirar dúvidas.',
    defaults: { combo: 'creme', align: 'left', pad: 'normal', shape: 'none' },
    fields: [
      { key: 'headline', label: 'Título da seção', type: 'text' },
      { key: 'sub',      label: 'Subtítulo', type: 'textarea' },
      {
        key: 'cards', label: 'Cartões', type: 'list', itemLabel: 'cartão',
        fields: [
          { key: 'titulo',     label: 'Título', type: 'text' },
          { key: 'texto',      label: 'Descrição', type: 'textarea' },
          { key: 'botaoLabel', label: 'Texto do botão', type: 'text' },
          { key: 'botaoHref',  label: 'Destino do botão', type: 'link' },
          { key: 'icone',      label: 'Ícone', type: 'text', hint: 'whatsapp, link, ou deixe vazio' },
        ],
      },
    ],
  },
  {
    id: 'faq', n: '12', label: 'Dúvidas frequentes',
    hint: 'Accordion de perguntas e respostas.',
    defaults: { combo: 'creme', align: 'left', pad: 'normal', shape: 'none' },
    fields: [
      { key: 'headline', label: 'Título da seção', type: 'text' },
      { key: 'texto',    label: 'Texto de abertura', type: 'textarea' },
      {
        key: 'itens', label: 'Perguntas', type: 'list', itemLabel: 'pergunta',
        fields: [
          { key: 'pergunta', label: 'Pergunta', type: 'text' },
          { key: 'resposta', label: 'Resposta', type: 'textarea' },
        ],
      },
    ],
  },
  {
    id: 'ctaFinal', n: '13', label: 'CTA final',
    hint: 'Última chamada, para quem chegou ao fim sem se decidir.',
    defaults: { combo: 'laranja', align: 'center', pad: 'normal', shape: 'topo' },
    fields: [
      { key: 'headline', label: 'Título', type: 'text' },
      { key: 'texto',    label: 'Texto', type: 'textarea' },
      ...linkFields(),
    ],
  },
  {
    id: 'footer', n: '14', label: 'Rodapé',
    hint: 'Dados da empresa, SAC, redes sociais e links legais.',
    defaults: { textura: true, combo: 'marrom', align: 'left', pad: 'normal', shape: 'none' },
    fields: [
      { key: 'logo',        label: 'Logo', type: 'image', hint: 'Em fundo marrom, use a versão clara da logo.' },
      {
        key: 'assinatura', label: 'Frase de assinatura', type: 'text',
        // Os padrões reproduzem exatamente como a frase já era desenhada.
        estilo: { align: 'right', peso: 'titulo', tam: 18 },
      },
      ...linkFields(),
      { key: 'empresa',     label: 'Razão social', type: 'text' },
      { key: 'endereco',    label: 'Endereço', type: 'textarea' },
      { key: 'cnpj',        label: 'CNPJ', type: 'text' },
      { key: 'sacEmail',    label: 'E-mail do SAC', type: 'text' },
      { key: 'sacWhatsapp', label: 'WhatsApp do SAC', type: 'text' },
      { key: 'horario',     label: 'Horário de atendimento', type: 'textarea' },
      { key: 'selo',        label: 'Selo de certificação', type: 'image' },
      {
        key: 'redes', label: 'Redes sociais', type: 'list', itemLabel: 'rede',
        fields: [
          { key: 'nome', label: 'Rede', type: 'text', hint: 'instagram, facebook, youtube, tiktok' },
          { key: 'href', label: 'Link', type: 'link' },
        ],
      },
      {
        key: 'legais', label: 'Links legais', type: 'list', itemLabel: 'link',
        fields: [
          { key: 'label', label: 'Texto', type: 'text' },
          { key: 'href',  label: 'Link', type: 'link' },
        ],
      },
      { key: 'copyright', label: 'Aviso de copyright', type: 'text' },
    ],
  },
]

export const BLOCK_BY_ID: Record<BlockId, BlockDef> = Object.fromEntries(
  BLOCKS.map(b => [b.id, b]),
) as Record<BlockId, BlockDef>

export const BLOCK_ORDER: BlockId[] = BLOCKS.map(b => b.id)

// ─── Config salva ──────────────────────────────────────────────────────────

export interface BlockStyle {
  combo: ComboName
  /** Só vale no fundo marrom, o único com duas cores de texto aprovadas. */
  textOn?: ColorName
  align: 'left' | 'center'
  /**
   * Aparência escolhida campo a campo, para os campos que declaram `estilo` no
   * blueprint. Mora no estilo, e não nos dados, porque `normalizeConfig` copia
   * o estilo inteiro — dos dados ele só copia as chaves que são campos do
   * bloco, e uma chave auxiliar aqui seria descartada a cada carga.
   */
  estiloCampos?: Record<string, EstiloCampo>
  mediaSide: 'left' | 'right'
  pad: PadName
  shape: ShapeName
  /**
   * Imagem de fundo da seção inteira, em `cover`.
   *
   * Diferente de `texturaImg`, que é um ladrilho repetido em opacidade baixa:
   * esta ocupa a lâmina toda e serve para quem prefere desenhar a arte inteira
   * fora e soltar de uma vez, em vez de compor texto + foto na grade.
   */
  fundoImg?: string
  /** Ponto focal da imagem de fundo, para o recorte não cortar o assunto. */
  fundoPos?: 'center' | 'top' | 'bottom' | 'left' | 'right'
  /**
   * Véu sobre a imagem, de 0 a 1, na COR DE FUNDO APROVADA da seção.
   *
   * É o que devolve contraste ao texto sem sair do guia — velar com preto
   * genérico injetaria uma quarta cor. Zero deixa a foto crua; nesse caso a
   * legibilidade é responsabilidade de quem montou a arte.
   */
  fundoVeu?: number
  /** Padrão de fundo sobre a cor chapada. */
  textura?: boolean
  /**
   * PNG próprio para o padrão de fundo. Vazio usa os chevrons embutidos, que
   * se pintam com a cor de texto do bloco.
   */
  texturaImg?: string
  /** Tamanho do ladrilho do padrão, em px. */
  texturaTam?: number
  /** Opacidade do padrão, de 0 a 1. */
  texturaOp?: number
  /** Altura do hero, em vh. */
  height?: number
}

export type ListItem = Record<string, string> & { _id: string }
export type BlockData = Record<string, string | ListItem[]>

export interface BlockState {
  enabled: boolean
  data: BlockData
  style: BlockStyle
}

export interface LandingConfig {
  version: 1
  order: BlockId[]
  blocks: Record<BlockId, BlockState>
  /** Fontes enviadas pelo painel, disponíveis para qualquer campo. */
  fontes?: FonteEnviada[]
}

const BASE_STYLE: BlockStyle = {
  combo: 'creme',
  align: 'left',
  mediaSide: 'right',
  pad: 'normal',
  shape: 'none',
  textura: false,
  // Declarados para o `defaultProps` do editor nascer com eles e os controles
  // não abrirem em branco. Não muda nenhuma landing salva: `normalizeConfig`
  // copia o `style` inteiro por cima desta base, e o desenho é duplamente
  // guardado — `Section` exige `fundoImg` para desenhar qualquer coisa, e exige
  // `fundoVeu > 0` DENTRO daquele ramo. Véu sem imagem é inerte.
  fundoImg: '',
  fundoPos: 'center',
  fundoVeu: 0,
}

function emptyData(def: BlockDef): BlockData {
  const data: BlockData = {}
  for (const f of def.fields) data[f.key] = isListField(f) ? [] : ''
  return data
}

/**
 * O blueprint vazio: todos os blocos presentes, na ordem canônica, habilitados
 * e sem nenhum conteúdo. É o que a landing serve enquanto ninguém editou nada.
 */
export function emptyConfig(): LandingConfig {
  const blocks = {} as Record<BlockId, BlockState>
  for (const def of BLOCKS) {
    blocks[def.id] = {
      enabled: true,
      data: emptyData(def),
      style: { ...BASE_STYLE, ...def.defaults },
    }
  }
  return { version: 1, order: [...BLOCK_ORDER], blocks }
}

/**
 * Reconcilia uma config vinda do banco com o blueprint atual: mantém o que já
 * foi preenchido e completa o que falta. Sem isso, acrescentar um bloco ou
 * campo novo quebraria as landings já salvas.
 */
export function normalizeConfig(raw: unknown): LandingConfig {
  const base = emptyConfig()
  if (!raw || typeof raw !== 'object') return base

  const inc = raw as Partial<LandingConfig>

  // Ordem: respeita a salva, descarta ids que não existem mais e acrescenta
  // no fim os blocos que surgiram depois.
  const savedOrder = Array.isArray(inc.order) ? inc.order.filter(id => id in base.blocks) : []
  const order = [...savedOrder, ...BLOCK_ORDER.filter(id => !savedOrder.includes(id))]

  const blocks = base.blocks
  for (const def of BLOCKS) {
    const saved = inc.blocks?.[def.id]
    if (!saved) continue
    const target = blocks[def.id]
    if (typeof saved.enabled === 'boolean') target.enabled = saved.enabled
    if (saved.style) {
      target.style = { ...target.style, ...saved.style }
      // O alinhamento por campo nasceu sozinho, numa chave `alignCampos`, antes
      // de virar o conjunto `estiloCampos`. Configs salvas naquela janela são
      // convertidas aqui, senão a escolha some da tela sem explicação.
      const s = target.style as BlockStyle & { alignCampos?: Record<string, AlignCampo> }
      if (s.alignCampos) {
        const migrado: Record<string, EstiloCampo> = { ...s.estiloCampos }
        for (const [campo, align] of Object.entries(s.alignCampos)) {
          migrado[campo] = { ...migrado[campo], align }
        }
        s.estiloCampos = migrado
        delete s.alignCampos
      }
      if (s.estiloCampos) s.estiloCampos = normalizaTamanhos(s.estiloCampos)
    }
    if (saved.data) {
      for (const f of def.fields) {
        const v = saved.data[f.key]
        if (isListField(f)) {
          if (Array.isArray(v)) target.data[f.key] = v as ListItem[]
        } else if (typeof v === 'string') {
          target.data[f.key] = v
        }
      }
    }
  }
  // Fontes enviadas. A limpeza acontece aqui, na entrada, e não na hora de
  // desenhar: o nome vai cru para dentro de um @font-face, e uma aspa perdida
  // não quebraria só a fonte — levaria junto o bloco <style> inteiro da página
  // pública. A URL é presa a /uploads/ para o campo não virar um jeito de
  // apontar a página para um host qualquer.
  const fontes = Array.isArray(inc.fontes)
    ? inc.fontes
        .filter(f => f && typeof f === 'object')
        .map(f => ({ nome: nomeFonteSeguro(String(f.nome ?? '')), url: String(f.url ?? '') }))
        .filter(f => f.nome && f.url.startsWith('/uploads/'))
    : []

  return { version: 1, order, blocks, fontes }
}

/**
 * O tamanho já foi uma escala fechada ('pequeno'…'destaque') antes de virar
 * número. Cada nome vira o px que aquela escala dava na tela grande, para uma
 * landing salva naquela janela não mudar de aparência sozinha. Número fora dos
 * limites é grampeado — vem de digitação, não de escolha.
 */
const TAM_LEGADO: Record<string, number> = {
  pequeno: 15, normal: 18, grande: 24, destaque: 32,
}

function normalizaTamanhos(campos: Record<string, EstiloCampo>): Record<string, EstiloCampo> {
  const saida: Record<string, EstiloCampo> = {}
  for (const [chave, est] of Object.entries(campos)) {
    const bruto: unknown = est?.tam
    let tam: number | undefined
    if (typeof bruto === 'string') tam = TAM_LEGADO[bruto]
    else if (typeof bruto === 'number' && Number.isFinite(bruto)) {
      tam = Math.min(TAM_MAX, Math.max(TAM_MIN, Math.round(bruto)))
    }
    saida[chave] = tam === undefined ? { ...est, tam: undefined } : { ...est, tam }
    if (saida[chave].tam === undefined) delete saida[chave].tam
  }
  return saida
}

/** Nome de família que pode entrar num @font-face sem escapar de dentro dele. */
export function nomeFonteSeguro(nome: string): string {
  return nome.replace(/[^\w \-]/g, '').replace(/\s+/g, ' ').trim().slice(0, 40)
}

// ─── Acesso tipado aos dados de um bloco ───────────────────────────────────

export function str(data: BlockData, key: string): string {
  const v = data[key]
  return typeof v === 'string' ? v : ''
}

export function list(data: BlockData, key: string): ListItem[] {
  const v = data[key]
  return Array.isArray(v) ? v : []
}

/**
 * Aparência efetiva de um campo: os padrões declarados no blueprint, cobertos
 * pelo que foi escolhido no painel. Só as chaves realmente escolhidas são
 * sobrescritas, então uma landing salva antes de um controle novo continua
 * sendo desenhada exatamente como era.
 */
export function estiloCampo(
  id: BlockId, style: BlockStyle, campo: string, subcampo?: string,
): EstiloCampo {
  const f = BLOCK_BY_ID[id]?.fields.find(x => x.key === campo)
  const padrao = (f && !isListField(f) && f.estilo) || {}
  return { ...padrao, ...style.estiloCampos?.[chaveEstilo(campo, subcampo)] }
}
